import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Skeleton,
  Spacer,
  Text,
  useToast,
} from '@chakra-ui/react'
import { useUpdateSystemConfigMutation } from '@stocker/codegen/configurator-backend'
import { FiRsDisk, FiRsPencil, FiRsShare } from '@stocker/ui-components/design-system'
import { asComponent } from '@stocker/ui-components/helpers'
import groupBy from 'lodash/groupBy'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { ExpertCheckModal } from '../../compounds/ExpertCheckModal/ExpertCheckModal'
import { useConfiguration } from '../ConfigurationProvider'
import type { IBasketItemProps } from '../SystemBasket/SystemBasket'
import { KmmiDrawing } from './KmmiDrawing'
import {
  KMMI_SYSTEM_CODE,
  KOMBILINE_LBS_SYSTEM_CODE,
  LBS_BOTTOM_CATEGORIES,
  LBS_SHAFT_CATEGORIES,
  LBS_TOP_CATEGORIES,
  drawsEveryCategory,
  lbsLoosePartCategories,
  tConnectorIsBase,
} from './drawingRules'

export interface ISystemSummaryProps {
  isLoaded?: {
    name?: boolean
  }
  currentConfigId: string
  items: IBasketItemProps[]
  currentConfigName?: string
  updateConfigMutation?: ReturnType<typeof useUpdateSystemConfigMutation>
  configuration?: Array<{
    title: string
    text: string
  }>
  defaultConfigurationName: string
  isExpertCheckRequested: boolean
  isExpertCheckAcknowledged: boolean
  isExpertCheckFinished: boolean
  isAdmin: boolean
  systemCode?: string
}

// Category keys the drawing's own slots do not name, grouped by where they belong in the stack.
// The strings are backend keys: mostly `ClassificationCodes`, plus `GasketService`, which is a
// NestJS class name. See apps/configurator-backend/src/system/system-codes.ts, a rename there
// empties a row and nothing fails at compile time. `rainHood:collar` is built in the KMMI and LBS
// system designs. LBS is the only system that reaches these lists with it or `insulationPipes`.
const MOUTH_CATEGORIES = ['rainHood', 'rainHood:collar', 'mouth', 'expansionJointElement']
const PIPE_CATEGORIES = ['insulationPipes', 'lengthElementsWithDrainLoop', 'spacers', 'exterierCleaningPort']
const BASE_CATEGORIES = ['removableConnection', 'flexConnector', 'GasketService', 'condensateTray']

export const SystemSummary: React.FC<ISystemSummaryProps> = ({
  isLoaded = { name: true },
  currentConfigId,
  items,
  currentConfigName,
  updateConfigMutation,
  configuration,
  defaultConfigurationName,
  isExpertCheckRequested,
  isExpertCheckAcknowledged,
  isExpertCheckFinished,
  isAdmin,
  systemCode,
}) => {
  const toast = useToast()
  const spacerZero = { base: '0', xl: '0' } // This was the spacer for DW Design

  const { formatMessage } = useIntl()
  const [editMode, setEditMode] = useState(false)
  const { configurationName, setConfigurationName } = useConfiguration()

  useEffect(() => {
    setConfigurationName(currentConfigName ?? defaultConfigurationName)
  }, [defaultConfigurationName, currentConfigName, setConfigurationName])

  const productsGroupedByCategory: Record<string, IBasketItemProps[] | undefined> = groupBy(
    items,
    (v) => v.category,
  )

  const ImageDisplay = ({
    items,
    mainImageWidth,
    secondaryImageWidth,
    containerProps = {},
    imageWrapperProps = {},
    imageAltText = 'product image',
    maxImageHeight,
  }: {
    items: IBasketItemProps[]
    mainImageWidth: any
    secondaryImageWidth: any
    containerProps?: object
    imageWrapperProps?: object
    imageAltText?: string
    maxImageHeight: string
  }) => {
    if (!items || items.length === 0) {
      return null
    }

    const mainImage = items[items.length - 1]
    const secondaryImages = items.slice(0, items.length - 1)

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
      e.currentTarget.style.display = 'none'
    }

    return (
      <Flex {...containerProps}>
        {secondaryImages.length > 0 && (
          <Flex alignItems="flex-end" justifyContent="flex-end" flexDirection="row" flexWrap="wrap">
            {secondaryImages.map((item) => (
              <Box key={item.sku || item.name} width={secondaryImageWidth} {...imageWrapperProps}>
                <img
                  src={`${item.image?.fullpath ?? ''}?width=200`}
                  alt={item.name || imageAltText}
                  onError={handleImageError}
                  style={{
                    maxHeight: maxImageHeight,
                    width: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            ))}
          </Flex>
        )}

        {mainImage && (
          <Box key={mainImage.sku || mainImage.name} width={mainImageWidth} {...imageWrapperProps}>
            <img
              src={`${mainImage.image?.fullpath ?? ''}?width=200`}
              alt={mainImage.name || imageAltText}
              onError={handleImageError}
              style={{
                maxHeight: maxImageHeight,
                width: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}
      </Flex>
    )
  }

  // Removed conditional styling variables for 25-50 as per request
  // const mrForLangeElementeBox = systemCode === 'SYS_DW_25-50' ? { base: '2px', xl: '10px' } : undefined;
  // const mrForLangeElementeFlex = systemCode === 'SYS_DW_25-50' ? { base: '10px', xl: '15px' } : undefined;
  // const mtForLangeElementeImages = systemCode === 'SYS_DW_25-50' ? { base: '-10px', xl: '-50px' } : { base: '0', xl: '0' };

  const renderRow = (key: string, items: IBasketItemProps[]) => (
    <ImageDisplay
      key={key}
      items={items}
      mainImageWidth={'50px'}
      secondaryImageWidth={'50px'}
      containerProps={{
        mt: spacerZero,
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
      }}
      maxImageHeight={'65px'}
    />
  )

  // Per-Zug systems suffix their keys with `:zug{n}`, which this exact lookup misses. KombiLine
  // Mantelstein draws its own layout instead, see `KmmiDrawing`.
  const renderCategories = (categories: string[]) => {
    if (!drawsEveryCategory(systemCode)) return []
    return categories.map((category) => renderRow(category, productsGroupedByCategory[category] ?? []))
  }

  const isLbs = systemCode === KOMBILINE_LBS_SYSTEM_CODE
  const renderLbsCategories = (categories: string[]) => (isLbs ? renderCategories(categories) : [])

  const drawCleaningPortAbove = tConnectorIsBase(
    systemCode,
    (productsGroupedByCategory.TConnectorService ?? []).map((item) => item.sku),
  )

  const tConnectorBlock = (productsGroupedByCategory.TransitionService ||
    productsGroupedByCategory.TConnectorService) && (
    <Flex mt={spacerZero} mr="5px" flexDirection="column" alignItems="flex-end" gap={2}>
      {productsGroupedByCategory.TransitionService && (
        <ImageDisplay
          items={productsGroupedByCategory.TransitionService}
          mainImageWidth={'50px'}
          secondaryImageWidth={'50px'}
          containerProps={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
          maxImageHeight={'65px'}
        />
      )}

      {productsGroupedByCategory.TConnectorService && (
        <ImageDisplay
          items={[...productsGroupedByCategory.TConnectorService]
            .sort((a, b) => b.price.amount - a.price.amount)
            .reverse()}
          mainImageWidth={'50px'}
          secondaryImageWidth={'50px'}
          containerProps={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
          maxImageHeight={'65px'}
        />
      )}
    </Flex>
  )

  const cleaningPortBlock = productsGroupedByCategory.CleaningPortService && (
    <ImageDisplay
      items={productsGroupedByCategory.CleaningPortService}
      mainImageWidth={'50px'}
      secondaryImageWidth={'50px'}
      containerProps={{
        mt: spacerZero,
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
      maxImageHeight={'65px'}
    />
  )

  return (
    <Flex direction="column" h="100%">
      <Box
        w="full"
        fontSize={{ base: 'lg', xl: 'xl' }}
        fontFamily="primary"
        color="primaryText.500"
        bg="primaryBackground.500"
      >
        <Flex align="center" justify="space-between" gap={2}>
          {editMode ? (
            <>
              <Flex flexDir="column" w="full" p={{ base: 3, xl: 4 }}>
                <Input
                  h="24px"
                  autoFocus
                  fontSize={{ base: 'lg', xl: 'xl' }}
                  onChange={(e) => {
                    setConfigurationName(e.currentTarget.value)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setEditMode(false)
                      if (configurationName.length > 0 && configurationName.length <= 40) {
                        updateConfigMutation?.mutate({
                          config: { systemConfigId: currentConfigId, name: configurationName },
                        })
                      } else {
                        setConfigurationName(currentConfigName ?? defaultConfigurationName)
                      }
                    }
                  }}
                  focusBorderColor="accent.500"
                  borderBottomColor="accent.500"
                  value={configurationName}
                  variant="flushed"
                  placeholder="Name der Konfiguration"
                />
                {configurationName.length === 0 && (
                  <Text fontFamily="secondary" fontSize="sm" color="accent.500">
                    {formatMessage({ id: 'configurator-name--tooltip-name-empty' })}
                  </Text>
                )}
                {configurationName.length > 40 && (
                  <Text fontFamily="secondary" fontSize="sm" color="accent.500">
                    {formatMessage({ id: 'configurator-name--tooltip-name-too-long' })}
                  </Text>
                )}
              </Flex>
              <Button
                size="sm"
                colorScheme="accent"
                leftIcon={<FiRsDisk />}
                onClick={() => {
                  setEditMode(false)
                  if (configurationName.length > 0 && configurationName.length <= 40) {
                    updateConfigMutation?.mutate({
                      config: { systemConfigId: currentConfigId, name: configurationName },
                    })
                  } else {
                    setConfigurationName(currentConfigName ?? defaultConfigurationName)
                  }
                }}
                mr={2}
                px={5}
                borderRadius="md"
              >
                {formatMessage({ id: '--save' })}
              </Button>
            </>
          ) : (
            <>
              <Skeleton m={{ base: 3, xl: 4 }} isLoaded={isLoaded.name} w="full">
                <Text
                  borderBottomColor="secondaryBackground.500"
                  borderBottomWidth={1}
                  onClick={() => {
                    setEditMode(!editMode)
                  }}
                >
                  {configurationName}
                </Text>
              </Skeleton>
              <Box
                py={{ base: 2, xl: 3 }}
                px={{ base: 3, xl: 4 }}
                onClick={() => {
                  setEditMode(!editMode)
                }}
                lineHeight={0}
              >
                <FiRsPencil cursor="pointer" />
              </Box>
            </>
          )}
        </Flex>
      </Box>
      {items.length === 0 && (
        <Text p={4} borderColor="secondaryText.50" borderWidth="2px" borderY={0}>
          {formatMessage({ id: 'configurator-summary--empty-notice' })}
        </Text>
      )}
      <Flex
        p={4}
        direction="row"
        gap={2}
        position="relative"
        borderColor="secondaryText.50"
        borderWidth="2px"
        borderTop={0}
        h="100%"
        overflowY="scroll"
      >
        <Box width="50%">
          {configuration
            ?.filter((item) => item.title !== '')
            .map((item) => (
              <Box key={item.title}>
                <Text my={2} fontFamily="primary">
                  {item.title}
                </Text>
                {asComponent(item.text)}
              </Box>
            ))}
        </Box>
        {/* Images */}
        <Box
          width="50%"
          position="sticky"
          top={0}
          right={0}
          display="flex"
          justifyContent="flex-end"
          alignItems="flex-end" // Align children to the end of the cross axis (bottom)
          flexDirection="column"
          overflowY="scroll"
          overflowX="hidden"
          pb="40px"
          pt="0px"
        >
          {systemCode === KMMI_SYSTEM_CODE ? (
            <KmmiDrawing items={items} />
          ) : (
            <>
              {/* OverRoofService */}
              {productsGroupedByCategory.OverRoofService && (
                <ImageDisplay
                  items={productsGroupedByCategory.OverRoofService}
                  mainImageWidth={'50px'}
                  secondaryImageWidth={'50px'}
                  containerProps={{
                    mt: spacerZero,
                    mr: '5px',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                  }}
                  maxImageHeight={'65px'}
                />
              )}

              {/* EndPartService */}
              {productsGroupedByCategory.EndPartService && (
                <ImageDisplay
                  items={productsGroupedByCategory.EndPartService}
                  mainImageWidth={'50px'}
                  secondaryImageWidth={'50px'}
                  containerProps={{
                    mt: spacerZero,
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                  }}
                  maxImageHeight={'65px'}
                />
              )}

              {renderCategories(MOUTH_CATEGORIES)}
              {renderLbsCategories(LBS_TOP_CATEGORIES)}

              {/* RoofDuctService */}
              {productsGroupedByCategory.RoofDuctService && (
                <ImageDisplay
                  items={productsGroupedByCategory.RoofDuctService}
                  mainImageWidth={'50px'}
                  secondaryImageWidth={'50px'}
                  containerProps={{
                    mt: spacerZero,
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                  }}
                  maxImageHeight={'65px'}
                />
              )}

              {/* LengthElementService-after-offset */}
              {productsGroupedByCategory['LengthElementService-after-offset'] && (
                <ImageDisplay
                  items={productsGroupedByCategory['LengthElementService-after-offset']}
                  mainImageWidth={'50px'}
                  secondaryImageWidth={'50px'}
                  containerProps={{
                    mt: spacerZero,
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                  }}
                  imageWrapperProps={{ mt: spacerZero }}
                  maxImageHeight={'65px'}
                />
              )}

              {/* LengthElementService */}
              {productsGroupedByCategory.LengthElementService && (
                <ImageDisplay
                  items={productsGroupedByCategory.LengthElementService}
                  mainImageWidth={'50px'}
                  secondaryImageWidth={'50px'}
                  containerProps={{
                    mt: spacerZero,
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                  }}
                  imageWrapperProps={{ mt: spacerZero }}
                  maxImageHeight={'65px'}
                />
              )}

              {renderCategories(PIPE_CATEGORIES)}
              {renderLbsCategories(LBS_SHAFT_CATEGORIES)}

              {/* OffsetService */}
              {productsGroupedByCategory.OffsetService && (
                <ImageDisplay
                  items={productsGroupedByCategory.OffsetService.filter(
                    (item) => !item.name.includes('Rohr'),
                  )}
                  mainImageWidth={'50px'}
                  secondaryImageWidth={'50px'}
                  containerProps={{
                    mt: spacerZero,
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                  }}
                  maxImageHeight={'65px'}
                />
              )}

              {/* WallMountingService */}
              {productsGroupedByCategory.WallMountingService && (
                <ImageDisplay
                  items={productsGroupedByCategory.WallMountingService}
                  mainImageWidth={'50px'}
                  secondaryImageWidth={'50px'}
                  containerProps={{
                    mt: spacerZero,
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                  }}
                  maxImageHeight={'65px'}
                />
              )}

              {drawCleaningPortAbove ? (
                <>
                  {cleaningPortBlock}
                  {tConnectorBlock}
                </>
              ) : (
                <>
                  {tConnectorBlock}
                  {cleaningPortBlock}
                </>
              )}

              {renderCategories(BASE_CATEGORIES)}

              {/* Combined FootingService and WallMountingService (original combined section) */}
              {(productsGroupedByCategory.FootingService ||
                productsGroupedByCategory.WallMountingService) && (
                <ImageDisplay
                  items={(() => {
                    const combinedItems: IBasketItemProps[] = []
                    let footingPartItem: IBasketItemProps | undefined

                    if (productsGroupedByCategory.WallMountingService) {
                      combinedItems.push(...productsGroupedByCategory.WallMountingService)
                    }

                    if (productsGroupedByCategory.FootingService) {
                      const footingNoFussTeil = productsGroupedByCategory.FootingService.filter(
                        (item) => {
                          if (item.name.includes('Fußteil')) {
                            footingPartItem = item
                            return false
                          }
                          return true
                        },
                      )
                      combinedItems.push(...footingNoFussTeil)
                    }

                    if (footingPartItem) {
                      combinedItems.push(footingPartItem)
                    }

                    if (combinedItems.length === 0) {
                      return []
                    }

                    return combinedItems
                  })()}
                  mainImageWidth={'50px'}
                  secondaryImageWidth={'50px'}
                  containerProps={{
                    mt: spacerZero,
                    flexDirection: 'row',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                  }}
                  maxImageHeight={'65px'}
                />
              )}

              {renderLbsCategories(LBS_BOTTOM_CATEGORIES)}
              {isLbs &&
                renderRow(
                  'KLBS:loose-parts',
                  lbsLoosePartCategories(Object.keys(productsGroupedByCategory)).flatMap(
                    (category) => productsGroupedByCategory[category] ?? [],
                  ),
                )}

              {/* DraughtRegulatorService */}
              {productsGroupedByCategory.DraughtRegulatorService && (
                <ImageDisplay
                  items={productsGroupedByCategory.DraughtRegulatorService.sort(
                    (a, b) => a.price.amount - b.price.amount,
                  )}
                  mainImageWidth={'50px'}
                  secondaryImageWidth={'50px'}
                  containerProps={{
                    mt: spacerZero,
                    display: 'flex',
                    alignItems: 'center',
                    mr: '5px',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                  }}
                  maxImageHeight={'65px'}
                />
              )}
            </>
          )}

          {Object.keys(productsGroupedByCategory).length !== 0 && (
            <Box position="absolute" right={0} bottom={0} mb={3}>
              <Text fontWeight="bold" fontSize="xs" mr={5}>
                {formatMessage({ id: 'configurator--symbolic-representation' })}
              </Text>
            </Box>
          )}
        </Box>
      </Flex>
      <Spacer />
      <Flex
        justify="flex-end"
        p={4}
        py={8}
        direction="column"
        gap={2}
        borderColor="secondaryText.50"
        borderWidth="2px"
        borderTop={0}
      >
        <HStack justify="space-between">
          <ExpertCheckModal
            configId={currentConfigId}
            isExpertCheckRequested={isExpertCheckRequested}
            isExpertCheckAcknowledged={isExpertCheckAcknowledged}
            isExpertCheckFinished={isExpertCheckFinished}
            isAdmin={isAdmin}
          />
          <Button
            size={{ base: 'sm', xl: 'md' }}
            variant="outline"
            colorScheme="secondaryText"
            leftIcon={<FiRsShare />}
            onClick={() => {
              navigator.clipboard.writeText(window.location.href)
              toast({
                title: formatMessage({ id: 'configurator-summary-toast--title' }),
                description: formatMessage({ id: 'configurator-summary-toast--description' }),
                status: 'success',
                duration: 4000,
                isClosable: true,
              })
            }}
          >
            {formatMessage({ id: '--share-configuration' })}
          </Button>
        </HStack>
      </Flex>
    </Flex>
  )
}
